import React from 'react';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import {
  Button,
  Col,
  IconWrapper,
  Image,
  MultiLineEllipsisText,
  Row,
  SupplierStatus,
  Tag,
  Typography,
} from 'components';
import {
  ClientTypeIcon,
  EditIconButton,
  GlobeIcon,
  LanguageIcon,
  SupplierTypeIcon,
  VerifiedIcon,
} from 'components/common/icons';
import { SupplierDetailsUpdate } from './edit';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';

const { Text } = Typography;

export const Details = React.memo(() => {
  const { isOpen, onModalClose, onModalOpen, supplierDetails } = useDetails();
  return (
    <>
      <Row gutter={[0, 16]}>
        <Col span={24} className={styles.main_info}>
          <Row justify={'space-between'} wrap={false}>
            <Col span={20}>
              <Row gutter={[0, 4]}>
                {supplierDetails.files?.LOGO && (
                  <Col className={styles.image_wrapper} span={24}>
                    <Image src={supplierDetails.files.LOGO} preview={false} alt={'Logo'} />
                  </Col>
                )}
                <Col span={24}>
                  <Text className={'font-size-12'} type={'secondary'}>
                    ID: {supplierDetails.uuid}
                  </Text>
                </Col>
                <Col span={24} className={styles.legal_name}>
                  <Row align={'middle'} wrap={false}>
                    <Text title={supplierDetails?.name} className={styles.name}>
                      {supplierDetails?.name}
                    </Text>
                    {supplierDetails.verified && <VerifiedIcon />}
                  </Row>
                </Col>
                <Col span={24}>
                  <SupplierStatus status={supplierDetails.status} tooltip={supplierDetails.reason} />
                </Col>
              </Row>
            </Col>
            <Col>
              <HasAccess role={PERMISSIONS.suppliers.supplier.write}>
                <Button type={'link'} icon={<EditIconButton />} onClick={onModalOpen} />
              </HasAccess>
            </Col>
          </Row>
          <Row gutter={[0, 4]} className={'margin-t-4'}>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {supplierDetails.formattedCreatedDate}
                </Text>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {supplierDetails.formattedLastModifiedDate}
                </Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.additional_info}>
          <Row gutter={[0, 6]}>
            {supplierDetails?.webSite && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <GlobeIcon />
                  <Button
                    style={{ padding: 0, margin: 0 }}
                    title={supplierDetails.webSite}
                    size={'small'}
                    type="link"
                    href={
                      supplierDetails.webSite?.startsWith('http')
                        ? supplierDetails.webSite
                        : `https://${supplierDetails.webSite}`
                    }
                    target="_blank"
                  >
                    <Text className={styles.cut_long_text} type={'secondary'}>
                      {supplierDetails.webSite}
                    </Text>
                  </Button>
                </Row>
              </Col>
            )}
            {supplierDetails.country?.name && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  {supplierDetails.country.fileLocation && <IconWrapper src={supplierDetails.country.fileLocation} />}
                  <Text type={'secondary'}>{supplierDetails.country.name}</Text>
                </Row>
              </Col>
            )}
            {supplierDetails.language?.name && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <LanguageIcon />
                  <Text type={'secondary'}>{supplierDetails.language?.name}</Text>
                </Row>
              </Col>
            )}
            {supplierDetails.industry?.name && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <ClientTypeIcon />
                  <Text type={'secondary'}>{supplierDetails.industry.name}</Text>
                </Row>
              </Col>
            )}
            {supplierDetails.type && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <SupplierTypeIcon />
                  <Text type={'secondary'}>{supplierDetails.type}</Text>
                </Row>
              </Col>
            )}
            {/*TODO system generated tags*/}
          </Row>
        </Col>
        <Col span={24} className={styles.manual_tags}>
          <Row className={styles.tag_container}>
            {supplierDetails.manualTags?.map((_tag: string, index: number) => (
              <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                {_tag}
              </Tag>
            ))}
          </Row>
        </Col>
        <Col span={24}>
          <MultiLineEllipsisText subTitle={'Additional Notes:'} text={supplierDetails.additionalNotes} />
        </Col>
      </Row>
      <SupplierDetailsUpdate isOpen={isOpen} onCancel={onModalClose} />
    </>
  );
});
